import React, { useEffect, useMemo, useState } from "react";
import { FaCheckCircle, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";

import {
  getVoteById,
  getUserBallotsForVote,
} from "../../../../../services/voteServices";

import {
  getQuestionTypeLabel,
  getStoredToken,
  normalizeQuestionType,
} from "../utils/userVoteUtils";

function pickFirstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function asString(value) {
  if (value === undefined || value === null) return "";
  return String(value);
}

function normalizeAnswersPayload(payload) {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload.answers)) {
    return payload.answers;
  }

  if (Array.isArray(payload.rows)) {
    return payload.rows;
  }

  if (Array.isArray(payload.ballots)) {
    return payload.ballots;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload.responses)) {
    return payload.responses;
  }

  if (payload.responses && typeof payload.responses === "object") {
    return Object.entries(payload.responses).flatMap(([questionId, value]) => {
      if (Array.isArray(value)) {
        return value.map((item) => ({
          question_id: questionId,
          raw_value: item,
        }));
      }

      return [
        {
          question_id: questionId,
          raw_value: value,
        },
      ];
    });
  }

  if (payload.response && typeof payload.response === "object") {
    return Object.entries(payload.response).flatMap(([questionId, value]) => {
      if (Array.isArray(value)) {
        return value.map((item) => ({
          question_id: questionId,
          raw_value: item,
        }));
      }

      return [
        {
          question_id: questionId,
          raw_value: value,
        },
      ];
    });
  }

  return [];
}

function getQuestionIdFromRow(row) {
  return pickFirstDefined(
    row?.question_id,
    row?.questionId,
    row?.vote_question_id,
    row?.voteQuestionId,
    row?.VoteQuestion?.id,
    row?.Question?.id,
    row?.question?.id
  );
}

function getChoiceIdFromRow(row) {
  return pickFirstDefined(
    row?.choice_id,
    row?.choiceId,
    row?.vote_choice_id,
    row?.voteChoiceId,
    row?.selected_choice_id,
    row?.selectedChoiceId,
    row?.Choice?.id,
    row?.VoteChoice?.id,
    row?.choice?.id,
    row?.raw_value
  );
}

function getChoiceTextFromRow(row) {
  return pickFirstDefined(
    row?.choice_text,
    row?.choiceText,
    row?.Choice?.choice_text,
    row?.Choice?.text,
    row?.VoteChoice?.choice_text,
    row?.VoteChoice?.text,
    row?.choice?.choice_text,
    row?.choice?.text
  );
}

function getOpenTextFromRow(row) {
  return pickFirstDefined(
    row?.answer_text,
    row?.answerText,
    row?.response_text,
    row?.responseText,
    row?.text_answer,
    row?.textAnswer,
    row?.answer,
    row?.value,
    row?.raw_value
  );
}

function isProbablyChoiceId(value, choices = []) {
  if (value === undefined || value === null || value === "") return false;

  return choices.some((choice) => String(choice.id) === String(value));
}

function getChoiceLabelById(choices = [], choiceId) {
  const found = choices.find((choice) => String(choice.id) === String(choiceId));

  return found?.choice_text || found?.text || found?.label || "";
}

export default function UserBallotAnswersDrawer({
  isOpen,
  voteId,
  userId,
  onClose,
}) {
  const [vote, setVote] = useState(null);
  const [answerRows, setAnswerRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = useMemo(() => getStoredToken(), []);

  useEffect(() => {
    if (!isOpen || !voteId || !userId) return;

    let alive = true;

    const load = async () => {
      try {
        setLoading(true);

        const [voteData, answersPayload] = await Promise.all([
          getVoteById(voteId),
          getUserBallotsForVote(userId, voteId, token),
        ]);

        if (!alive) return;

        setVote(voteData || null);
        setAnswerRows(normalizeAnswersPayload(answersPayload));

        console.log("Réponses utilisateur brutes :", answersPayload);
        console.log("Réponses utilisateur normalisées :", normalizeAnswersPayload(answersPayload));
      } catch (error) {
        console.error("Erreur chargement réponses utilisateur :", error);
        toast.error("Impossible de charger vos réponses.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [isOpen, voteId, userId, token]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  const answersByQuestion = useMemo(() => {
    const map = new Map();

    answerRows.forEach((row) => {
      const questionId = getQuestionIdFromRow(row);

      if (questionId === undefined || questionId === null) return;

      const key = String(questionId);

      if (!map.has(key)) {
        map.set(key, {
          openTexts: [],
          choiceIds: [],
          choiceTexts: [],
          rawValues: [],
        });
      }

      const entry = map.get(key);

      const openText = getOpenTextFromRow(row);
      const choiceId = getChoiceIdFromRow(row);
      const choiceText = getChoiceTextFromRow(row);
      const rawValue = row?.raw_value;

      if (openText !== undefined && openText !== null && openText !== "") {
        entry.openTexts.push(openText);
      }

      if (choiceId !== undefined && choiceId !== null && choiceId !== "") {
        entry.choiceIds.push(choiceId);
      }

      if (choiceText !== undefined && choiceText !== null && choiceText !== "") {
        entry.choiceTexts.push(choiceText);
      }

      if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
        entry.rawValues.push(rawValue);
      }
    });

    return map;
  }, [answerRows]);

  const answersView = useMemo(() => {
    if (!vote) return [];

    const questions = Array.isArray(vote.questions) ? vote.questions : [];

    return questions.map((question) => {
      const type = normalizeQuestionType(question.type);
      const choices = Array.isArray(question.choices) ? question.choices : [];

      const entry = answersByQuestion.get(String(question.id)) || {
        openTexts: [],
        choiceIds: [],
        choiceTexts: [],
        rawValues: [],
      };

      if (type === "open") {
        const value =
          entry.openTexts.find((item) => asString(item).trim()) ||
          entry.rawValues.find((item) => asString(item).trim()) ||
          "—";

        return {
          question,
          type,
          value: asString(value),
        };
      }

      if (type === "single") {
        const directChoiceText = entry.choiceTexts.find((item) =>
          asString(item).trim()
        );

        if (directChoiceText) {
          return {
            question,
            type,
            value: directChoiceText,
          };
        }

        const choiceId = entry.choiceIds.find((item) =>
          isProbablyChoiceId(item, choices)
        );

        if (choiceId) {
          return {
            question,
            type,
            value: getChoiceLabelById(choices, choiceId) || "—",
          };
        }

        const rawValue =
          entry.rawValues.find((item) => asString(item).trim()) ||
          entry.openTexts.find((item) => asString(item).trim());

        return {
          question,
          type,
          value: rawValue ? asString(rawValue) : "—",
        };
      }

      if (type === "multiple") {
        const labels = [];

        entry.choiceTexts.forEach((label) => {
          if (label && !labels.includes(label)) {
            labels.push(label);
          }
        });

        entry.choiceIds.forEach((choiceId) => {
          const label = getChoiceLabelById(choices, choiceId);

          if (label && !labels.includes(label)) {
            labels.push(label);
          }
        });

        entry.rawValues.forEach((rawValue) => {
          const labelFromId = getChoiceLabelById(choices, rawValue);
          const label = labelFromId || asString(rawValue);

          if (label && !labels.includes(label)) {
            labels.push(label);
          }
        });

        return {
          question,
          type,
          value: labels,
        };
      }

      return {
        question,
        type,
        value: "—",
      };
    });
  }, [vote, answersByQuestion]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Mes réponses
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {vote?.title || "Vote / sondage"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="h-32 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                />
              ))}
            </div>
          ) : !vote ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
              Impossible de charger vos réponses.
            </div>
          ) : answersView.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
              Aucune question trouvée pour ce vote.
            </div>
          ) : (
            <div className="space-y-5">
              {answersView.map(({ question, type, value }, index) => (
                <article
                  key={question.id}
                  className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950"
                >
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">
                        Question {index + 1}
                      </p>

                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60">
                        {getQuestionTypeLabel(type)}
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <ReactQuill
                        value={question.question_text || ""}
                        readOnly
                        theme="bubble"
                      />
                    </div>

                    {type === "open" && (
                      <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                        {value || "—"}
                      </div>
                    )}

                    {type === "single" && (
                      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-sm font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/60">
                        <FaCheckCircle />
                        {value || "—"}
                      </div>
                    )}

                    {type === "multiple" && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((label, choiceIndex) => (
                            <span
                              key={`${question.id}-${choiceIndex}`}
                              className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-sm font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
                            >
                              <FaCheckCircle />
                              {label}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">
                            —
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-3 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}